import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $applyNodeReplacement, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from "lexical";
import { JSX, useCallback, useState } from "react";
import { NodeDeleteButton } from "@/components/Editor/NodeDeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BaseAppNode, registerBaseAppNodeType } from "./BaseAppNode";
import { WindowDecorator } from "./WindowNode";

// Register this node type with BaseAppNode
registerBaseAppNodeType("action-item");

export type SerializedActionItemNode = Spread<
  {
    type: "action-item";
    version: 1;
    key: string;
    action: string;
    priority: "high" | "medium" | "low";
    status: "todo" | "in-progress" | "done";
    dueDate?: string;
    assignee?: string;
    notes?: string;
  },
  SerializedLexicalNode
>;

export type ActionItemPriority = "high" | "medium" | "low";
export type ActionItemStatus = "todo" | "in-progress" | "done";

/**
 * ActionItemNode - 行动项节点
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 */
export class ActionItemNode extends BaseAppNode {
  __action: string;
  __priority: ActionItemPriority;
  __status: ActionItemStatus;
  __dueDate?: string;
  __assignee?: string;
  __notes?: string;

  static getType(): string {
    return "action-item";
  }

  static clone(node: ActionItemNode): ActionItemNode {
    return new ActionItemNode(node.__action, node.__priority, node.__status, node.__dueDate, node.__assignee, node.__notes, node.__key);
  }

  constructor(
    action?: string,
    priority?: ActionItemPriority,
    status?: ActionItemStatus,
    dueDate?: string,
    assignee?: string,
    notes?: string,
    key?: NodeKey,
  ) {
    super(false, key); // false = block-level node
    this.__action = action ?? "";
    this.__priority = priority ?? "medium";
    this.__status = status ?? "todo";
    this.__dueDate = dueDate;
    this.__assignee = assignee;
    this.__notes = notes;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = "action-item-node-container";
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <ActionItemDecorator node={this} />;
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedActionItemNode): ActionItemNode {
    const node = $createActionItemNode(
      serializedNode.action,
      serializedNode.priority,
      serializedNode.status,
      serializedNode.dueDate ?? "",
      serializedNode.assignee ?? "",
      serializedNode.notes ?? "",
    );
    return node;
  }

  exportJSON(): SerializedActionItemNode {
    return {
      type: "action-item",
      version: 1,
      key: this.__key,
      action: this.__action,
      priority: this.__priority,
      status: this.__status,
      dueDate: this.__dueDate,
      assignee: this.__assignee,
      notes: this.__notes,
    };
  }

  // ========== Getter/Setter ==========

  getPriority(): ActionItemPriority {
    return this.__priority;
  }

  setPriority(priority: ActionItemPriority): void {
    const writable = this.getWritable();
    writable.__priority = priority;
  }

  getStatus(): ActionItemStatus {
    return this.__status;
  }

  setStatus(status: ActionItemStatus): void {
    const writable = this.getWritable();
    writable.__status = status;
  }

  getDueDate(): string {
    return this.__dueDate || "";
  }

  setDueDate(dueDate: string): void {
    const writable = this.getWritable();
    writable.__dueDate = dueDate;
  }

  getAction(): string {
    return this.__action;
  }

  setAction(action: string): void {
    const writable = this.getWritable();
    writable.__action = action;
  }

  getAssignee(): string {
    return this.__assignee || "";
  }

  setAssignee(assignee: string): void {
    const writable = this.getWritable();
    writable.__assignee = assignee;
  }

  getNotes(): string {
    return this.__notes || "";
  }

  setNotes(notes: string): void {
    const writable = this.getWritable();
    writable.__notes = notes;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    return `[行动项] ${this.__action} - ${this.__priority} - ${this.__status}`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof ActionItemNode)) {
      return false;
    }
    return this.__action === other.__action && this.__priority === other.__priority && this.__status === other.__status;
  }
}

/**
 * 行动项节点编辑器组件
 */
function ActionItemDecorator({ node }: { node: ActionItemNode }) {
  const [editor] = useLexicalComposerContext();

  const [action, setAction] = useState(node.getAction());
  const [priority, setPriority] = useState(node.getPriority());
  const [status, setStatus] = useState(node.getStatus());
  const [dueDate, setDueDate] = useState(node.getDueDate());
  const [assignee, setAssignee] = useState(node.getAssignee());
  const [notes, setNotes] = useState(node.getNotes());

  const priorityConfig = {
    high: { label: "高", color: "bg-red-100 text-red-800 border-red-300" },
    medium: { label: "中", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
    low: { label: "低", color: "bg-green-100 text-green-800 border-green-300" },
  };

  // 检查是否过期
  const isOverdue = () => {
    if (!dueDate || status === "done") return false;
    try {
      const due = new Date(dueDate);
      return due < new Date();
    } catch {
      return false;
    }
  };

  // Debounced update helper - runs in editor.update() context
  const debounceUpdate = useCallback(
    (updateFn: () => void) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          editor.update(updateFn);
        });
      });
    },
    [editor],
  );

  return (
    <WindowDecorator node={node} className={`my-4 p-4 rounded-lg border bg-card shadow-sm ${status === "done" ? "opacity-75" : ""}`}>
      {(handleDelete) => (
        <>
          {/* Header with delete button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">行动项</h3>
            <NodeDeleteButton onClick={handleDelete} title="删除节点" />
          </div>

          {/* 行动描述和完成状态 */}
          <div className="flex items-start justify-between mb-4 gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">行动描述</Label>
              <Input
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  debounceUpdate(() => node.setAction(e.target.value));
                }}
                placeholder="描述需要采取的行动..."
                className={`mt-1 ${status === "done" ? "line-through" : ""}`}
              />
            </div>
            <div className="flex-shrink-0">
              <Label className="text-sm font-medium">状态</Label>
              <Button
                variant={status === "done" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const nextStatus: ActionItemStatus = status === "done" ? "todo" : "done";
                  setStatus(nextStatus);
                  debounceUpdate(() => node.setStatus(nextStatus));
                }}
                className="mt-1"
              >
                {status === "done" ? "已完成" : "待办"}
              </Button>
            </div>
          </div>

          {/* 优先级和元数据 */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* 优先级 */}
            <div>
              <Label className="text-sm font-medium">优先级</Label>
              <div className="flex gap-2 mt-1">
                {(["high", "medium", "low"] as const).map((p) => (
                  <Button
                    key={p}
                    variant={priority === p ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setPriority(p);
                      debounceUpdate(() => node.setPriority(p));
                    }}
                  >
                    {priorityConfig[p].label}
                  </Button>
                ))}
              </div>
            </div>

            {/* 截止日期 */}
            <div>
              <Label className="text-sm font-medium">截止日期</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  debounceUpdate(() => node.setDueDate(e.target.value));
                }}
                className="mt-1"
              />
              {isOverdue() && <p className="text-xs text-red-600 dark:text-red-400 mt-1">⚠️ 已逾期</p>}
            </div>
          </div>

          {/* 负责人 */}
          <div className="mb-4">
            <Label className="text-sm font-medium">负责人</Label>
            <Input
              value={assignee}
              onChange={(e) => {
                setAssignee(e.target.value);
                debounceUpdate(() => node.setAssignee(e.target.value));
              }}
              placeholder="分配给谁？"
              className="mt-1"
            />
          </div>

          {/* 备注 */}
          <div>
            <Label className="text-sm font-medium">备注</Label>
            <Textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                debounceUpdate(() => node.setNotes(e.target.value));
              }}
              placeholder="添加备注或详细信息..."
              className="mt-1"
              rows={3}
            />
          </div>
        </>
      )}
    </WindowDecorator>
  );
}

/**
 * 创建 ActionItemNode
 */
export function $createActionItemNode(
  action: string = "",
  priority: ActionItemPriority = "medium",
  status: ActionItemStatus = "todo",
  dueDate: string = "",
  assignee: string = "",
  notes: string = "",
): ActionItemNode {
  const node = new ActionItemNode(action, priority, status, dueDate, assignee, notes);
  return $applyNodeReplacement(node);
}
